import React, { useState } from "react";
import { ArrowDownUp, List, Trash } from "react-bootstrap-icons";
import { Col, Dropdown, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { weekendApi } from "../../../../api/weekend";
import { HGBugsnagNotify } from "../../../../helpers/bugsnag";
import { getStatusCode } from "../../../../helpers/errors";
import { AssignmentNotification } from "../../../../types/scheduling/meetings";
import { Congregation, PublicTalkAssignment, Speaker } from "../../../../types/scheduling/weekend";
import { NotificationDates } from "../../../../types/date";
import { CongSettings } from "../../../../types/scheduling/settings";
import { AssignmentSummary, SearchAssigneeModal } from "../../RecentAssignments";
import { SaveResult, SaveStatus } from "../../../saveStatus";
import QueryKeys from "../../../../api/queryKeys";
import { OutSpeakerDropdown } from "./dropdowns/OutSpeakerDropdown";
import { CongregationDropdown } from "./dropdowns/CongregationDropdown";
import { TalkDropdown } from "./dropdowns/TalkDropdown";
import { congCompare, useUserMap } from "../../common";

interface OutgoingAssignmentProps {
  out: PublicTalkAssignment;
  langGroupId: number;
  setOut: (set: PublicTalkAssignment) => void;
  speakers: Speaker[];
  congregations: Congregation[];
  setSelectedID: (set: number) => void;
  setShowDeleteConfirm: (set: boolean) => void;
  notifications?: AssignmentNotification[];
  notificationDates: NotificationDates;
  setShowMoveModal: (set: boolean) => void;
  congSettings: CongSettings;
  userAssignmentMap: Map<number, AssignmentSummary[]>;
  pwSchedules: any[];
  absences?: any[];
  assignmentMap: Map<number, AssignmentSummary[]>;
}

export const OutgoingAssignment: React.FC<OutgoingAssignmentProps> = (props) => {
  const { t } = useTranslation();
  const [speaker, setSpeaker] = useState(props.out.speaker?.id ?? 0);
  const [talk, setTalk] = useState(props.out.public_talk?.number ?? 0);
  const [congId, setCongId] = useState(props.out.congregation?.id ?? 0);
  const [saveResult, setSaveResult] = useState(SaveResult.None);
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const userMap = useUserMap();
  const speakerUsers = props.speakers.flatMap((s) => userMap.get(s.userId ?? 0) ?? []);
  const selectedSpeaker = props.speakers.find((s) => s.id === speaker);

  const meetingDate = weekOfString(props.out.date, props.congregations.find((c) => c.id === congId)?.wm_dow ?? 7);

  const save = async (speaker: number, cong: number, talk: number, force?: boolean) => {
    // force lets the cong save happen regardless of whether anything else is specified
    if (!force && (!talk || !cong || !speaker)) return;
    setSaving(true);
    try {
      await weekendApi.setOutSchedule(
        {
          id: props.out.id,
          date: props.out.date,
          assignee: speaker,
          congregation: cong,
          talk: talk,
        },
        props.langGroupId,
      );
      setSaveResult(SaveResult.Success);
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.WMCSchedules],
      });
    } catch (err: any) {
      if (getStatusCode(err) === 409) {
        setSaveResult(SaveResult.Success);
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.WMCSchedules],
        });
      } else {
        setSaveResult(SaveResult.Failure);
        console.error("wm outgoing save error", err);
        HGBugsnagNotify("wmOutgoingSave", err);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-3 row p-0" key={props.out.id}>
      <SearchAssigneeModal
        date={meetingDate}
        assignee={userMap.get(selectedSpeaker?.userId ?? 0)}
        partType="publicTalkOut"
        possibleAssignees={speakerUsers.filter((u) => u.id !== selectedSpeaker?.userId)}
        show={showAssignmentModal}
        setShow={setShowAssignmentModal}
        assign={async (userId) => {
          setSpeaker(props.speakers.find((s) => s.userId === userId)?.id ?? 0);
          setTalk(0);
        }}
        assignmentMap={props.assignmentMap}
        userMap={userMap}
        langGroupId={props.langGroupId}
        heading={
          <div>
            <div className="mx-auto">
              <h5>{t("schedules.weekend.speaker.0")}</h5>
            </div>
          </div>
        }
      />
      <Row>
        <Col lg={4}>
          <OutSpeakerDropdown
            className="d-flex flex-column"
            speakers={props.speakers}
            absences={props.absences}
            out={props.out}
            meetingDate={meetingDate}
            selected={speaker}
            saving={saving}
            notifications={props.notifications}
            notificationDates={props.notificationDates}
            congSettings={props.congSettings}
            userAssignmentMap={props.userAssignmentMap}
            outCongregation={props.congregations.find((c) => c.id === congId)}
            pwSchedules={props.pwSchedules}
            setSpeaker={(speaker) => {
              //when we change the speaker, we still need to know what talk, so we don't save it
              setSpeaker(speaker);
              setTalk(0);
            }}
            setShowAssignmentModal={setShowAssignmentModal}
          />
        </Col>
        <Col lg={4}>
          <CongregationDropdown
            congs={props.congregations.sort(congCompare)}
            excludeMyCong
            langGroupId={props.langGroupId}
            selected={congId}
            override={congId}
            setCong={async (cong) => {
              setCongId(cong);
              if (cong) {
                // as long as we have a cong selected, save it
                await save(speaker, cong, talk, true);
              }
            }}
          />
        </Col>
        <Col lg={3}>
          <TalkDropdown
            speakers={props.speakers}
            disabled={!canUpdateWeekendSchedules()}
            selectedSpeaker={speaker}
            selectedTalk={talk}
            setTalk={async (talk) => {
              setTalk(talk);
              await save(speaker, congId, talk);
            }}
            date={meetingDate}
          />
        </Col>
        <Col lg={1}>
          <Dropdown className="float-end">
            <Dropdown.Toggle
              variant="secondary"
              className="py-2 d-flex align-items-center"
              size="sm"
              aria-label={t("userinfo.button.actions")}
            >
              {saveResult === SaveResult.None ? (
                <List />
              ) : (
                <SaveStatus
                  noSpace
                  size={13.5}
                  saveResult={saveResult}
                  saveKey={props.out.id}
                  setSaveResult={setSaveResult}
                />
              )}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                disabled={!canUpdateWeekendSchedules()}
                onClick={() => {
                  props.setOut(props.out);
                  props.setShowMoveModal(true);
                }}
              >
                <ArrowDownUp /> {t("schedules.weekend.move-assignment")}
              </Dropdown.Item>
              <Dropdown.Item
                disabled={!canUpdateWeekendSchedules()}
                onClick={() => {
                  props.setSelectedID(props.out.id);
                  props.setShowDeleteConfirm(true);
                }}
              >
                <Trash color="red" /> {t("schedules.weekend.delete-outgoing")}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Col>
      </Row>
    </div>
  );
};

// Helper functions and constants that need to be imported
import { weekOfString } from "../../../../helpers/dateHelpers";
import { canUpdateWeekendSchedules } from "../wm_common";
